import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import {
  monthlyPrayerTimes, calculatePrayerTimes,
  PRAYER_LABELS_ID, type PrayerKey,
} from "@/lib/prayer/calc";
import {
  type Location, type IndonesiaTZ, tzOffsetHours, tzLabel,
  getKotaInProvinsi, getKecamatanInKota, getProvinsi,
  PROVINSI, KOTA_ALL, KECAMATAN_ALL,
} from "@/data/locations";
import { todayISO, parseISO, MONTH_NAMES_ID, DAY_NAMES_SHORT_ID, dayOfWeek } from "@/lib/calendar/dates";
import { gregorianToHijri, HIJRI_MONTHS } from "@/lib/calendar/hijri";
import { getHijriEvent } from "@/lib/calendar/hijri-events";
import { HijriEventBadge } from "@/components/calendar/HijriEventBadge";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Compass, CalendarDays, CalendarRange } from "lucide-react";
import { NextPrayerCard } from "./NextPrayerCard";
import { getPrayerCopy, displayLocationTitle } from "@/lib/prayer/copy";
import { FavoriteButton } from "./FavoriteButton";
import { pushHistory } from "@/lib/prayer/favorites";
import { DateHeaderTiles } from "./DateHeaderTiles";
import { DataSourceLine } from "./DataSourceLine";
import { LocationQuickSearch } from "./LocationQuickSearch";
import { LazyMount } from "@/components/LazyMount";
import { buildLocationFacts, locationPlaceJsonLd } from "@/lib/prayer/location-copy";
import { prayerEntities } from "@/lib/seo/entities";
import { PrayerBreadcrumb } from "./PrayerBreadcrumb";
import { PrayerCrossNav } from "./PrayerCrossNav";

// Lazy-loaded: only fetched when user opens dialogs / scrolls to the section.
const PrayerSettings = lazy(() => import("./PrayerSettings").then((m) => ({ default: m.PrayerSettings })));
const PrayerShareDialog = lazy(() => import("./PrayerShareDialog").then((m) => ({ default: m.PrayerShareDialog })));
const PrayerPdfButton = lazy(() => import("@/components/PrayerPdfButton").then((m) => ({ default: m.PrayerPdfButton })));
const YearView = lazy(() => import("./YearView").then((m) => ({ default: m.YearView })));
const RelatedAreaGrid = lazy(() => import("./RelatedAreaGrid").then((m) => ({ default: m.RelatedAreaGrid })));
const PrayerLocationToolsRail = lazy(() =>
  import("./PrayerLocationToolsRail").then((m) => ({ default: m.PrayerLocationToolsRail })),
);

export type Variant = "sholat" | "imsak";

const VARIANT_COLS: Record<Variant, PrayerKey[]> = {
  sholat: ["imsak", "subuh", "terbit", "dhuha", "dzuhur", "ashar", "maghrib", "isya"],
  imsak: ["imsak", "subuh", "terbit", "dzuhur", "ashar", "maghrib", "isya"],
};

function locShortPath(variant: Variant, loc: Location): string {
  if (loc.kind === "provinsi") return `/jadwal-${variant}/${loc.slug}`;
  if (loc.kind === "kota") return `/jadwal-${variant}/${loc.slug}`;
  return `/jadwal-${variant}/${loc.parentSlug}/${loc.slug}`;
}

export function PrayerSchedulePage({
  variant, location, defaultNotice,
}: { variant: Variant; location: Location; defaultNotice?: string }) {
  return (
    <SiteShell>
      <Body variant={variant} location={location} defaultNotice={defaultNotice} />
    </SiteShell>
  );
}

function Body({ variant, location, defaultNotice }: { variant: Variant; location: Location; defaultNotice?: string }) {
  const today = parseISO(todayISO())!;
  const [view, setView] = useState({ y: today.y, m: today.m });
  const [mode, setMode] = useState<"month" | "year">("month");
  const [tzOverride, setTzOverride] = useState<IndonesiaTZ | null>(null);
  const settingsBtnRef = useRef<HTMLButtonElement | null>(null);

  // Restore tz override per-location from localStorage (idle-deferred)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const run = () => {
      const saved = window.localStorage.getItem(`prayer:tz:${location.slug}`);
      if (saved === "WIB" || saved === "WITA" || saved === "WIT") setTzOverride(saved);
      else setTzOverride(null);
    };
    const ric = (window as any).requestIdleCallback as ((cb: () => void) => number) | undefined;
    if (ric) ric(run);
    else setTimeout(run, 0);
  }, [location.slug]);

  // Push to history (for /jadwal-sholat dashboard) — idle-deferred
  useEffect(() => {
    if (typeof window === "undefined") return;
    const run = () =>
      pushHistory({
        variant, slug: location.slug,
        kecSlug: location.kind === "kecamatan" ? location.slug : undefined,
        name: displayLocationTitle(location),
        href: locShortPath(variant, location),
      });
    const ric = (window as any).requestIdleCallback as ((cb: () => void) => number) | undefined;
    if (ric) ric(run);
    else setTimeout(run, 200);
  }, [variant, location.slug]);

  function handleTzChange(tz: IndonesiaTZ | null) {
    setTzOverride(tz);
    if (typeof window === "undefined") return;
    if (tz) window.localStorage.setItem(`prayer:tz:${location.slug}`, tz);
    else window.localStorage.removeItem(`prayer:tz:${location.slug}`);
  }

  const tzHours = tzOffsetHours(tzOverride ?? location.tz);
  const cols = VARIANT_COLS[variant];
  const copy = getPrayerCopy(variant, location);

  const month = useMemo(
    () => monthlyPrayerTimes({ y: view.y, m: view.m, lat: location.lat, lng: location.lng, tzHours }),
    [view.y, view.m, location.lat, location.lng, tzHours],
  );
  const todayTimes = useMemo(
    () => calculatePrayerTimes({ y: today.y, m: today.m, d: today.d, lat: location.lat, lng: location.lng, tzHours }),
    [today.y, today.m, today.d, location.lat, location.lng, tzHours],
  );

  const provName = getProvinsi(location.provinceSlug)?.name ?? "";
  const kotaList = location.kind === "provinsi" ? getKotaInProvinsi(location.slug) : [];
  const kecList = location.kind === "kota" ? getKecamatanInKota(location.slug) : [];

  const titlePrefix = variant === "sholat" ? "Jadwal Sholat" : "Jadwal Imsakiyah";
  const displayName = displayLocationTitle(location);
  const effectiveTz = tzOverride ?? location.tz;

  const prevMonth = () => setView((v) => v.m === 1 ? { y: v.y - 1, m: 12 } : { y: v.y, m: v.m - 1 });
  const nextMonth = () => setView((v) => v.m === 12 ? { y: v.y + 1, m: 1 } : { y: v.y, m: v.m + 1 });

  const oppositeVariant: Variant = variant === "sholat" ? "imsak" : "sholat";

  return (
    <>
      <PrayerBreadcrumb variant={variant} location={location} />
      <header className="rounded-xl bg-prayer/10 p-5 ring-1 ring-prayer/20">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-prayer/15 px-2 py-0.5 text-xs font-medium text-prayer">
              <Compass className="h-3 w-3" /> {tzLabel(effectiveTz)} • {location.lat.toFixed(2)}, {location.lng.toFixed(2)}
            </span>
            <h1 className="mt-2 font-display text-3xl tracking-tight md:text-4xl">
              {titlePrefix} {displayName}
            </h1>
            <DataSourceLine
              location={location}
              tz={effectiveTz}
              onChangeLocation={() => settingsBtnRef.current?.click()}
            />
            <DateHeaderTiles y={today.y} m={today.m} d={today.d} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <FavoriteButton variant={variant} location={location} />
            <Suspense fallback={null}>
              <PrayerShareDialog variant={variant} location={location} />
              <PrayerSettings
                variant={variant}
                location={location}
                tzOverride={tzOverride}
                onTzChange={handleTzChange}
                triggerRef={settingsBtnRef}
              />
              <PrayerPdfButton variant={variant} location={location} defaultMode={mode === "year" ? "year" : "month"} year={view.y} month={view.m} day={today.d} />
            </Suspense>
          </div>
        </div>
        <div className="mt-4">
          <LocationQuickSearch variant={variant} />
        </div>
        {defaultNotice && (
          <p className="mt-3 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            {defaultNotice}
          </p>
        )}
        <p className="sr-only">
          {titlePrefix} {displayName}. Hari ini {today.d} {MONTH_NAMES_ID[today.m - 1]} {today.y}.
          {location.kind !== "provinsi" && ` Berada di ${provName}.`}
        </p>
      </header>
      <PrayerCrossNav active={variant} location={location} />

      <NextPrayerCard location={location} variant={variant} />

      <section className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        {cols.map((k) => (
          <div key={k} className="rounded-lg border border-border bg-card p-3 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{PRAYER_LABELS_ID[k]}</div>
            <div className="mt-1 font-display text-2xl text-prayer">{todayTimes[k]}</div>
          </div>
        ))}
      </section>

      {/* View toggle */}
      <div className="mt-6 flex items-center gap-2">
        <div className="inline-flex rounded-md border border-border bg-card p-0.5">
          <button
            type="button" onClick={() => setMode("month")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm",
              mode === "month" ? "bg-prayer text-prayer-foreground" : "hover:bg-accent",
            )}
          >
            <CalendarDays className="h-4 w-4" /> Bulan
          </button>
          <button
            type="button" onClick={() => setMode("year")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm",
              mode === "year" ? "bg-prayer text-prayer-foreground" : "hover:bg-accent",
            )}
          >
            <CalendarRange className="h-4 w-4" /> Tahun {view.y}
          </button>
        </div>
      </div>

      {mode === "month" ? (
        <section className="mt-3 rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between gap-3 border-b border-border p-3">
            <button type="button" onClick={prevMonth} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-sm hover:bg-accent" aria-label="Bulan sebelumnya">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h2 className="text-base font-semibold">
              {titlePrefix} {MONTH_NAMES_ID[view.m - 1]} {view.y}
            </h2>
            <button type="button" onClick={nextMonth} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-sm hover:bg-accent" aria-label="Bulan berikutnya">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-card text-left shadow-[0_1px_0_0_var(--border)]">
                <tr>
                  <th className="bg-card px-3 py-2">Tanggal</th>
                  {cols.map((k) => (
                    <th key={k} className="bg-card px-3 py-2 text-center">{PRAYER_LABELS_ID[k]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {month.map((row) => {
                  const p = parseISO(row.iso)!;
                  const isToday = row.iso === todayISO();
                  const dn = DAY_NAMES_SHORT_ID[dayOfWeek(p.y, p.m, p.d)];
                  const h = gregorianToHijri(p.y, p.m, p.d);
                  const ev = getHijriEvent(h.month, h.day);
                  return (
                    <tr key={row.iso} className={cn("border-t border-border transition-colors", isToday ? "bg-prayer/10 ring-1 ring-prayer/30" : "hover:bg-prayer/5")}>
                      <td className={cn("whitespace-nowrap px-3 py-2", isToday && "font-semibold text-prayer")}>
                        <div>
                          {dn}, {p.d} {MONTH_NAMES_ID[p.m - 1]}
                          {isToday && <span className="ml-1.5 rounded-full bg-prayer px-1.5 text-[10px] text-prayer-foreground">Hari ini</span>}
                        </div>
                        <div className="text-[11px] font-normal text-muted-foreground">
                          {h.day} {HIJRI_MONTHS[h.month - 1]}
                        </div>
                        {ev && (
                          <div className="mt-0.5">
                            <HijriEventBadge event={ev} size="sm" />
                          </div>
                        )}
                      </td>
                      {cols.map((k) => (
                        <td key={k} className={cn("px-3 py-2 text-center font-mono tabular-nums", isToday && "font-semibold text-prayer")}>
                          {row.times[k]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="border-t border-border p-3 text-xs text-muted-foreground">
            Perhitungan: metode Kementerian Agama RI (Subuh 20°, Isya 18°, Asar Syafi'i, ihtiyat 2 menit, Imsak = Subuh − 10 menit). Akurasi ±1 menit.
          </p>
        </section>
      ) : (
        <div className="mt-3">
          <Suspense fallback={<div style={{ minHeight: 600 }} />}>
            <YearView location={location} year={view.y} cols={cols} tzOverride={tzOverride} />
          </Suspense>
          <p className="mt-3 text-xs text-muted-foreground">
            Tampilan tahun {view.y}. Ketuk header bulan untuk lipat/buka. Gunakan tombol PDF di atas untuk export setahun.
          </p>
        </div>
      )}

      {(kecList.length > 0 || kotaList.length > 0) && (
        <LazyMount minHeight={300}>
          <Suspense fallback={null}>
            {kecList.length > 0 && (
              <RelatedAreaGrid variant={variant} parent={location} items={kecList} childKind="kecamatan" />
            )}
            {kotaList.length > 0 && (
              <RelatedAreaGrid variant={variant} parent={location} items={kotaList} childKind="kota" />
            )}
          </Suspense>
        </LazyMount>
      )}
      <LocationFactsBlock variant={variant} location={location} />
      <LazyMount minHeight={400} className="mt-6">
        <section
          aria-labelledby="prayer-pseo-heading"
          className="rounded-lg border border-border bg-card p-6 leading-7 md:leading-8"
        >
          <h2 id="prayer-pseo-heading" className="mb-3 text-lg font-semibold text-foreground">
            {copy.introHeading}
          </h2>
          <p className="text-base text-foreground/85">{copy.introBody}</p>
          <h3 className="mt-6 mb-3 text-base font-semibold text-foreground">{copy.tipsHeading}</h3>
          <ul className="ml-5 list-disc space-y-2 text-base text-foreground/85">
            {copy.tips.map((t) => <li key={t}>{t}</li>)}
          </ul>
          <h3 className="mt-6 mb-3 text-base font-semibold text-foreground">
            FAQ Seputar {variant === "sholat" ? "Jadwal Sholat" : "Imsakiyah"} {displayName}
          </h3>
          <dl className="space-y-5">
            {copy.faqs.map((f) => (
              <div key={f.q}>
                <dt className="text-base font-semibold text-foreground">{f.q}</dt>
                <dd className="mt-1 text-base text-foreground/80">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </LazyMount>

      <section className="mt-6 grid gap-4 md:grid-cols-2">
        <Link to={locShortPath(oppositeVariant, location) as never} className="rounded-lg border border-border bg-card p-4 hover:border-prayer">
          <h3 className="font-semibold">{variant === "sholat" ? "Lihat Jadwal Imsakiyah" : "Lihat Jadwal Sholat"} {displayName}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {variant === "sholat" ? "Khusus puasa Ramadan & sahur." : "Jadwal sholat lima waktu lengkap."}
          </p>
        </Link>
        <Link to={`/jadwal-${variant}/lokasi` as never} className="rounded-lg border border-border bg-card p-4 hover:border-prayer">
          <h3 className="font-semibold">Indeks semua lokasi</h3>
          <p className="mt-1 text-sm text-muted-foreground">{PROVINSI.length} provinsi · {KOTA_ALL.length}+ kota · {KECAMATAN_ALL.length}+ kecamatan se-Indonesia.</p>
        </Link>
      </section>

      <LazyMount minHeight={200}>
        <Suspense fallback={null}>
          <PrayerLocationToolsRail variant={variant} />
        </Suspense>
      </LazyMount>
    </>
  );
}

function LocationFactsBlock({ variant, location }: { variant: Variant; location: Location }) {
  const facts = buildLocationFacts(variant, location);
  const placeLd = locationPlaceJsonLd(location);
  const aboutLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `Jadwal ${variant === "imsak" ? "Imsakiyah" : "Sholat"} ${location.name}`,
    inLanguage: "id-ID",
    isPartOf: { "@type": "WebSite", name: "Hari.co.id", url: "https://hari.co.id/" },
    about: prayerEntities(),
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1"] },
  };
  return (
    <section className="mt-6 rounded-lg border border-border bg-card p-5">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(placeLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutLd) }} />
      <h2 className="mb-3 font-display text-lg">Profil Lokasi</h2>
      <p className="text-sm text-muted-foreground">{facts.paragraph}</p>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {facts.facts.map((f) => (
          <div key={f.label} className="rounded-md bg-background/60 p-3">
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{f.label}</dt>
            <dd className="mt-0.5 text-sm font-medium">{f.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
